import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { ControlProps } from '../../../../Form';
import { getDynamicOptionsOrFallback, getMultilistboxValue, getOptionFromId } from '../../utils';
import { getFacilitiesDict } from './getFacilitiesOptions';
import FilterContainer from '../../FilterContainer';
import type { AccommodationFacilities } from '../../../../../utils/api/filtersAPIResponseTypes';
import type { CommonProps } from '../CommonProps';
import getFacilitiesContainer from '../getFacilitiesContainer';
import { defaultFacilities } from './utils';
import { createSearchParamsAccommodationFacilities } from './getFacilitiesFromQuery';

type GetFacilitiesField = {
  searchParams: SearchParams;
  commonProps: CommonProps;
  t: TranslationFunction;
};

const getFacilitiesOptions = ({
  accommodationFacilities,
  t,
  isWaiting,
}: {
  accommodationFacilities?: AccommodationFacilities;
  t: TranslationFunction;
  isWaiting: boolean;
}) => {
  const facilities = getDynamicOptionsOrFallback(accommodationFacilities, defaultFacilities);
  const facilitiesDict = getFacilitiesDict(t);

  return facilities
    .filter((x) => !!getOptionFromId(x, facilitiesDict))
    .map((x) => ({
      value: x,
      label: `${getOptionFromId(x, facilitiesDict)?.text}`,
      ...(accommodationFacilities
        ? {
            addLabel: accommodationFacilities[x] ? `${accommodationFacilities[x]}` : '0',
          }
        : {}),
      disabled: isWaiting || (accommodationFacilities && !accommodationFacilities[x]),
    }));
};

const getFacilitiesField = ({ searchParams, commonProps, t }: GetFacilitiesField): ControlProps => {
  const { dynamicFiltersResponse, isWaiting } = commonProps;
  const accommodationFacilities = dynamicFiltersResponse ? dynamicFiltersResponse.accommodationFacilities : undefined;
  const query = createSearchParamsAccommodationFacilities(searchParams);
  const value = getMultilistboxValue(query.accomodation_facilities, true);
  const facilitiesOptions = getFacilitiesOptions({
    accommodationFacilities,
    t,
    isWaiting,
  });

  return getFacilitiesContainer({
    fieldName: URLParameters.ACCOMODATION_FACILITIES,
    containerName: FilterContainer.FacilitiesContainer,
    containerTitle: t('vacation.search.filters.facilities.title'),
    options: facilitiesOptions,
    value,
    t,
    searchParams,
    fieldClassName: 'facilities-listbox',
    ...commonProps,
    hasTwoColumns: !commonProps.isMobile && !commonProps.isSidebar,
    // ...(!commonProps.isHorizontal ? { limit: 15 } : {}),
  });
};

export default getFacilitiesField;
